/**
 * Property Type Factory
 * 
 * This factory is responsible for creating the correct PropertyTypeHandler
 * instance based on the property type.
 * 
 * Benefits:
 * - Centralized handler creation logic
 * - Easy to add new property types
 * - Ensures type safety
 * - Provides caching for performance
 */

import { Injectable } from '@angular/core';
import { PropertyType } from '../../shared/definitions/property';
import { PropertyTypeHandler, PropertyCategory } from './base/property-type-handler.abstract';

// Import all handler implementations
import { ApartmentHandler } from './implementations/apartment-handler';
import { HouseHandler } from './implementations/house-handler';
import { VillaHandler } from './implementations/villa-handler';
import { OfficeHandler } from './implementations/office-handler';
import { WarehouseHandler } from './implementations/warehouse-handler';
import { RetailHandler } from './implementations/retail-handler';
import { RetailWarehouseHandler } from './implementations/retail-warehouse-handler';
import { LandHandler } from './implementations/land-handler';

/**
 * Factory service for creating property type handlers
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyTypeFactory {
  // Cache handlers for performance
  private handlerCache = new Map<PropertyType, PropertyTypeHandler>();

  /**
   * Get the handler for a specific property type
   * Uses caching to avoid creating multiple instances
   */
  getHandler(propertyType: PropertyType | string): PropertyTypeHandler {
    // Normalize the property type string
    const normalizedType = this.normalizePropertyType(propertyType);
    
    // Check cache first
    if (this.handlerCache.has(normalizedType)) {
      return this.handlerCache.get(normalizedType)!;
    }

    // Create new handler
    const handler = this.createHandler(normalizedType);
    
    // Cache it
    this.handlerCache.set(normalizedType, handler);
    
    return handler;
  }

  /**
   * Get handlers for multiple property types at once
   */
  getHandlers(propertyTypes: (PropertyType | string)[]): PropertyTypeHandler[] {
    return propertyTypes.map(type => this.getHandler(type));
  }

  /**
   * Get all available property types with their handlers
   */
  getAllPropertyTypes(): { type: PropertyType; handler: PropertyTypeHandler }[] {
    return Object.values(PropertyType).map(type => ({
      type,
      handler: this.getHandler(type)
    }));
  }

  /**
   * Check if a property type is valid/supported
   */
  isValidPropertyType(propertyType: string): boolean {
    try {
      const normalized = this.normalizePropertyType(propertyType);
      return Object.values(PropertyType).includes(normalized);
    } catch {
      return false;
    }
  }

  /**
   * Get property types by category
   */
  getPropertyTypesByCategory(category: string): PropertyTypeHandler[] {
    return this.getAllPropertyTypes()
      .map(({ handler }) => handler)
      .filter(handler => handler.category === category);
  }

  /**
   * Clear the handler cache (useful for testing or if handlers need to be recreated)
   */
  clearCache(): void {
    this.handlerCache.clear();
  }

  /**
   * Create a new handler instance based on property type
   */
  private createHandler(propertyType: PropertyType): PropertyTypeHandler {
    switch (propertyType) {
      case PropertyType.Apartment:
        return new ApartmentHandler();
      
      case PropertyType.House:
        return new HouseHandler();
      
      case PropertyType.Villa:
        return new VillaHandler();
      
      case PropertyType.Office:
        return new OfficeHandler();
      
      case PropertyType.Warehouse:
        return new WarehouseHandler();
      
      case PropertyType.Retail:
        return new RetailHandler();
      
      case PropertyType.RetailWarehouse:
        return new RetailWarehouseHandler();
      
      case PropertyType.Land:
        return new LandHandler();
      
      case PropertyType.Other:
      default:
        // For 'Other' or unknown types, use a default handler
        return this.createDefaultHandler(propertyType);
    }
  }

  /**
   * Create a default handler for unknown or 'Other' property types
   * This provides basic functionality for types not explicitly implemented
   */
  private createDefaultHandler(propertyType: PropertyType): PropertyTypeHandler {
    // Create an anonymous class that extends PropertyTypeHandler
    return new (class extends PropertyTypeHandler {
      readonly propertyType = propertyType;
      readonly category = PropertyCategory.Other;
      readonly displayName = 'Other Property';
      readonly pluralName = 'Other Properties';
      readonly icon = 'home_work';
      readonly description = 'General property type';

      getRoomTypes() {
        return [];
      }

      getFeatures() {
        return [];
      }

      getMainCharacteristics() {
        return ['surface', 'parkingSpaces'];
      }

      getValidationRules() {
        return [];
      }
    })();
  }

  /**
   * Normalize property type string to enum value
   */
  private normalizePropertyType(propertyType: PropertyType | string): PropertyType {
    // If it's already a PropertyType enum value, return it
    if (Object.values(PropertyType).includes(propertyType as PropertyType)) {
      return propertyType as PropertyType;
    }

    // Try to match case-insensitively
    const lowerType = propertyType.toString().toLowerCase();
    
    for (const [key, value] of Object.entries(PropertyType)) {
      if (value.toLowerCase() === lowerType || key.toLowerCase() === lowerType) {
        return value;
      }
    }

    // If no match found, return 'Other'
    console.warn(`Unknown property type: ${propertyType}. Defaulting to 'Other'.`);
    return PropertyType.Other;
  }
}

